import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

export interface ConsoleError {
  message: string;
  stack?: string;
  timestamp: Date;
  level: "error" | "warn";
}

export interface NetworkError {
  url: string;
  method: string;
  status: number;
  statusText: string;
  requestBody?: any;
  responseBody?: any;
  timestamp: Date;
}

@Injectable({
  providedIn: "root",
})
export class BugReportService {
  private consoleErrors: ConsoleError[] = [];
  private networkErrors: NetworkError[] = [];
  private maxErrors = 10; // Keep only the most recent 10 errors

  constructor(private http: HttpClient) {
    this.interceptConsoleErrors();
  }

  private interceptConsoleErrors(): void {
    // Store original console methods
    const originalError = console.error;
    const originalWarn = console.warn;

    // Override console.error
    console.error = (...args: any[]) => {
      this.addConsoleError("error", args);
      originalError.apply(console, args);
    };

    // Override console.warn
    console.warn = (...args: any[]) => {
      this.addConsoleError("warn", args);
      originalWarn.apply(console, args);
    };

    // Listen for unhandled errors
    window.addEventListener("error", (event) => {
      this.addConsoleError("error", [
        event.message,
        event.filename,
        event.lineno,
        event.colno,
        event.error,
      ]);
    });

    // Listen for unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      this.addConsoleError("error", ["Unhandled Promise Rejection:", event.reason]);
    });
  }

  private addConsoleError(level: "error" | "warn", args: any[]): void {
    const message = args
      .map((arg) => (typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)))
      .join(" ");

    const error: ConsoleError = {
      message,
      stack: args.find((arg) => arg instanceof Error)?.stack,
      timestamp: new Date(),
      level,
    };

    this.consoleErrors.push(error);

    // Keep only the most recent errors
    if (this.consoleErrors.length > this.maxErrors) {
      this.consoleErrors = this.consoleErrors.slice(-this.maxErrors);
    }
  }

  addNetworkError(error: HttpErrorResponse, requestBody?: any, method?: string): void {
    const networkError: NetworkError = {
      url: error.url || "Unknown URL",
      method: method || "Unknown Method",
      status: error.status,
      statusText: error.statusText,
      requestBody: this.toReadable(requestBody),
      responseBody: this.toReadable(error.error),
      timestamp: new Date(),
    };

    this.networkErrors.push(networkError);

    // Keep only the most recent errors
    if (this.networkErrors.length > this.maxErrors) {
      this.networkErrors = this.networkErrors.slice(-this.maxErrors);
    }
  }

  /**
   * Turns a request/response body into something the bug report can actually serialize. A
   * FormData, File, Blob, binary payload or ProgressEvent has no enumerable properties, so
   * JSON.stringify collapses it to `{}` (or a tree of empty arrays) and the email loses the data.
   * Plain values pass through unchanged.
   */
  private toReadable(value: any): any {
    if (value === null || value === undefined) return null;

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }

    if (typeof FormData !== "undefined" && value instanceof FormData) {
      const fields: { [key: string]: string[] } = {};
      value.forEach((entry, key) => {
        const text =
          typeof File !== "undefined" && entry instanceof File
            ? `file: ${entry.name} (${entry.size} bytes${entry.type ? ", " + entry.type : ""})`
            : String(entry);
        (fields[key] = fields[key] || []).push(text);
      });
      return { __type: "FormData", fields };
    }

    if (typeof File !== "undefined" && value instanceof File) {
      return { __type: "File", name: value.name, size: value.size, type: value.type };
    }

    if (typeof Blob !== "undefined" && value instanceof Blob) {
      return { __type: "Blob", size: value.size, type: value.type };
    }

    if (typeof ProgressEvent !== "undefined" && value instanceof ProgressEvent) {
      return { __type: "ProgressEvent", type: value.type };
    }

    if (value instanceof ArrayBuffer) {
      return { __type: "ArrayBuffer", byteLength: value.byteLength };
    }

    if (ArrayBuffer.isView(value)) {
      return { __type: "TypedArray", byteLength: value.byteLength };
    }

    return value;
  }

  private async captureScreenshot(): Promise<string> {
    try {
      // Load html2canvas on demand so it isn't bundled into the main chunk.
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(document.body, {
        height: window.innerHeight,
        width: window.innerWidth,
        useCORS: true,
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
      return "";
    }
  }

  async generateBugReport(): Promise<{
    consoleErrors: ConsoleError[];
    networkErrors: NetworkError[];
    screenshot: string;
    userAgent: string;
    url: string;
    timestamp: Date;
  }> {
    const screenshot = await this.captureScreenshot();

    return {
      consoleErrors: [...this.consoleErrors],
      networkErrors: [...this.networkErrors],
      screenshot,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
    };
  }

  async sendBugReport(description?: string): Promise<void> {
    const bugReport = await this.generateBugReport();

    // Send bug report to server
    await this.http
      .post("/api/BugReport", {
        description,
        consoleErrors: bugReport.consoleErrors,
        networkErrors: bugReport.networkErrors,
        screenshot: bugReport.screenshot,
        userAgent: bugReport.userAgent,
        url: bugReport.url,
        timestamp: bugReport.timestamp,
      })
      .toPromise();

    alert("Bug report sent successfully. Thank you for your feedback!");
  }

  clearErrors(): void {
    this.consoleErrors = [];
    this.networkErrors = [];
  }
}
